'use client'

import React from 'react'
import { Person } from '@/payload-types'
import Image from 'next/image'
import { isMedia } from '@/lib/utils'
import {
  Button,
  Sheet,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components'
import { Mail, Phone, Plus } from 'lucide-react'
import { SheetContent } from '@/components/ui/sheet'
import { RichText } from '@payloadcms/richtext-lexical/react'
import Link from 'next/link'

interface SheetPersonProps {
  person: Person
}

const initials = (str: string) =>
  str
    .split(' ')
    .filter(Boolean)
    .map((word) => word[0])
    .join('')

function SheetPerson({ person }: Readonly<SheetPersonProps>): React.ReactNode {
  const photo = person.photo

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="default"
          size="icon-sm"
          className="rounded-full ml-auto hover:cursor-pointer"
        >
          <Plus strokeWidth={1} />
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader className="mt-12">
          <SheetTitle className="text-xl">{person.name}</SheetTitle>
          <SheetDescription className="leading-tight">{person.position}</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-2">
          <figure className="relative bg-muted aspect-square w-full flex justify-center items-center">
            {photo && isMedia(photo) ? (
              <Image src={photo.url!} alt={photo.alt || person.name} fill objectFit="cover" />
            ) : (
              <figcaption className="cursor-default flex justify-center items-center w-40 h-40 font-semibold text-2xl rounded-full bg-foreground/10">
                {initials(person.name)}
              </figcaption>
            )}
          </figure>
          {person.bio && <RichText data={person.bio} className="prose prose-neutral p-3" />}
          {person.email || person.phone ? (
            <ul className="flex flex-col gap-4 p-3">
              {person.email ? (
                <li className="flex flex-row gap-2 items-center">
                  <span className="p-2 bg-foreground rounded-full text-background">
                    <Mail strokeWidth={1} size={20} />
                  </span>
                  <Link href={`mail:${person.email}`}>{person.email}</Link>
                </li>
              ) : null}
              {person.phone ? (
                <li className="flex flex-row gap-2 items-center">
                  <span className="p-2 bg-foreground rounded-full text-background">
                    <Phone strokeWidth={1} size={20} />
                  </span>
                  <Link href={`tel:${person.phone}`}>{person.phone}</Link>
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  )
}

export { SheetPerson, type SheetPersonProps }
